import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { FormInput, FormRow } from '../formRow/FormRow'
import GlobalSpinner from '../globalsSpinner/GlobalSpinner'
import { getPlayerSystemMonitor, PlayerMonitor } from '../../../api/playersApi'
import { converFromBytes, sortByNameString, sortByNumber } from '../../../services/common'
import SortIndicator from '../sortIndicator/SortIndicator'
import { getChannelsShort } from '../../../api/channelsApi'
import { Guid } from '../../../api/baseApi'
import Select, { MultiValue } from "react-select";
import { formatDate, formatTime, timeAgo } from '../../../services/dateService'

type Option<T> = { value: T, label: string}
type SortBy = "" | "Player" | "Network" | "CPU" | "GPU" | "Space" | "Memory" | "LatestHeartbeat" | "Version";

const PlayerSystemMonitor:FC = () => {
    let [isLoading, setIsLoading] = useState(false);
    let [playerSystemMonitorData, setPlayerSystemMonitorData] = useState<PlayerMonitor[]>([]);

    let [searchString, setSearchString] = useState("");

    let [filteredNetworks, setFilteredNetworks ] = useState<Option<Guid>[]>([]);
    let [selectedNetworks, setSelectedNetworks] = useState<Guid[]>([]);

    let [sortBy, setSortBy] = useState<SortBy>('');
    let [isAscSort, setIsAscSort] = useState<boolean>(false);

    let [isPageLoad, setIsPageLoad] = useState(false)

    let searchStringRef = useRef(searchString);
    let selectedNetworksRef = useRef(selectedNetworks);

    useEffect(() => {
        const fetch = async () => {
            setIsLoading(true);
            await Promise.all([updatePlayerSystemMonitor(), updateNetworks()]);
            setIsLoading(false)
            setIsPageLoad(true)
        }

        fetch();
        let refetchInterval = setInterval(async () => {
            await updatePlayerSystemMonitor();
        }, 15000)

        return () => {
            clearInterval(refetchInterval)
        }
    }, [])

    useEffect(() => {
        searchStringRef.current = searchString;
        selectedNetworksRef.current = selectedNetworks;
        let timeoutId = setTimeout(()=> {
            setSortBy("")
            setIsAscSort(true)
            isPageLoad && updatePlayerSystemMonitor();
        }, 1000)

        return () => {
            clearTimeout(timeoutId)
        }
    }, [searchString, selectedNetworks]);

    const updatePlayerSystemMonitor = async () => {
        let res = await getPlayerSystemMonitor({
            searchString: searchStringRef.current,
            networks: selectedNetworksRef.current
        });

        if (res.type == "succes") {
            setPlayerSystemMonitorData(res.data.items);
        }
    }

    const updateNetworks = async () => {
        let result = await getChannelsShort();
        if(result.type === "succes") {
            let t = result.data.items.map(el => {
                return {
                    label: el.name,
                    value: el.id
                }
            })
            setFilteredNetworks(t)
        }
    }

    const onChangeSelectedNetwork = (newValue: MultiValue<Option<Guid>>) => {
        if(newValue){
            let newIds = newValue.map(el => el.value)
            setSelectedNetworks(newIds);
        } else {
            setSelectedNetworks([]);
        }
    }

    const getStatusClass = (percentage: number) => {
        if (percentage >= 0 && percentage < 33) {
            return "ok";
        } else if (percentage >= 33 && percentage < 66) {
            return "warn";
        } else {
            return "danger";
        }
    }

    const getStatusClassForHeartbeat = (mins: number) => {
        if (mins >= 0 && mins < 1) {
            return "ok";
        } else if (mins >= 1 && mins < 5) {
            return "warn";
        } else {
            return "danger";
        }
    }

    const calcPercentRatio = (a: number, b:number) => (a/b * 100).toFixed(1)

    const sortByStringField = (x: PlayerMonitor, y: PlayerMonitor) =>{
        let tempX, tempY;
        switch(sortBy){
            case "Player" : {
                tempX = x.playerName;
                tempY = y.playerName
                break;
            }
            case "Network" : {
                tempX = x.networkName;
                tempY = y.networkName
                break;
            }
            case "LatestHeartbeat" : {
                tempX = x.latestHeartbeat;
                tempY = y.latestHeartbeat
                break;
            }
            case "Version" : {
                tempX = x.version;
                tempY = y.version
                break;
            }
            default : {
                tempX = ""; tempY =""
            }
        }
        let result = sortByNameString(tempX,tempY);
        if(!isAscSort) result*=-1;
        return result
    }

    const sortByNumberField = (x: PlayerMonitor, y: PlayerMonitor) =>{
        let tempX: number, tempY: number;
        switch(sortBy){
            case "CPU" : {
                tempX = x.cpuUsage;
                tempY = y.cpuUsage
                break;
            }
            case "GPU" : {
                tempX = x.gpuUsage;
                tempY = y.gpuUsage
                break;
            }
            case "Space" : {
                tempX = +calcPercentRatio(converFromBytes(x.availableSpaceBytes, 'GB'), converFromBytes(x.totalSpaceBytes, "GB"));
                tempY = +calcPercentRatio(converFromBytes(y.availableSpaceBytes, 'GB'), converFromBytes(y.totalSpaceBytes, "GB"));
                break;
            }
            case "Memory" : {
                tempX = +calcPercentRatio(converFromBytes(x.memoryUsedKB, 'MB'), converFromBytes(x.totalMemoryKB, "MB"));
                tempY = +calcPercentRatio(converFromBytes(y.memoryUsedKB, 'MB'), converFromBytes(y.totalMemoryKB, "MB"));
                break;
            }
            default : {
                tempX = 0; tempY = 0
            }
        }
        let result = sortByNumber(tempX,tempY);
        if(!isAscSort) result*=-1;
        return result
    }

    const sortByDate = (x: PlayerMonitor, y: PlayerMonitor) => {
        const dateA = new Date(x.latestHeartbeat as any).getTime();
        const dateB = new Date(y.latestHeartbeat as any).getTime();

        let result = sortByNumber(dateA,dateB);
        if(!isAscSort) result*=-1;
        return result
    }
    

    const sortedPlyerMonitor = useMemo(() => {
        let sortFn;
        switch(sortBy){
            case "Player" :
            case "Network" : 
            case "Version" : {
                sortFn = sortByStringField; 
                break;
            }
            case "CPU" :
            case "GPU" :
            case "Space" :
            case "Memory" : {
                sortFn = sortByNumberField; 
                break;
            }
            case "LatestHeartbeat" : {
                sortFn = sortByDate; 
                break;
            }
        }
        
        return [...playerSystemMonitorData].sort(sortFn)
    }, [playerSystemMonitorData, sortBy, isAscSort]);

    const renderPlayerMonitor = (data: PlayerMonitor[]) => {
        return data.map((el, i) => {

            let spaceAvailable = +converFromBytes(el.availableSpaceBytes, 'GB').toFixed(2)
            let spaceTotal = +converFromBytes(el.totalSpaceBytes, "GB").toFixed(2)
            let spacePercent = +calcPercentRatio(spaceAvailable, spaceTotal)
            let memoryUsed = +converFromBytes(el.memoryUsedKB, "MB").toFixed(2)
            let memoryTotal = +converFromBytes(el.totalMemoryKB, "MB").toFixed(2)
            let memoryPercent = +calcPercentRatio(memoryUsed, memoryTotal)
            let timeObj = timeAgo(el.latestHeartbeat)
            let mins = timeObj.type == "minute" ? timeObj.value : 6

            return <tr key={i}>
                <td>{el.playerName}</td>
                <td>{el.networkName}</td>
                <td className={getStatusClass(el.cpuUsage)}>{el.cpuUsage}%</td>
                <td className={getStatusClass(el.gpuUsage)}>{el.gpuUsage}%</td>
                <td className={getStatusClass(100-spacePercent)}>{spaceAvailable}/{spaceTotal} {`(${spacePercent}%)`}</td>
                <td className={getStatusClass(memoryPercent)}>{memoryUsed}/{memoryTotal} {`(${memoryPercent}%)`}</td>
                <td className={getStatusClassForHeartbeat(mins)}>{formatDate(el.latestHeartbeat)} {formatTime(el.latestHeartbeat)}  <span style={{fontSize:"12px"}}>{timeObj.agoString}</span></td>
                <td>{el.version}</td>
            </tr>
        })
    }

    const checkCurrentSortColumn = (sortBy: SortBy, currentSort: SortBy) => sortBy === currentSort

    const setSortData = (sortByNew: SortBy) => {
        if(sortByNew !== sortBy){
            setIsAscSort(true);
            setSortBy(sortByNew);
        }
        else {
            setIsAscSort(!isAscSort);
        }
    }

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-20 w-100">
                <h2 style={{margin:0}}>Player System Monitor</h2>

                <div className="d-flex gap15" style={{width:"600px"}}>
                    <FormRow>
                        <FormInput 
                            errorMessage = ""
                            id = "alert_search"
                            isError = {false}
                            title = "Search &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"
                            onChangeValue = {(v) => {setSearchString(v);}}
                            value = {searchString}
                        />
                    </FormRow>
                    <FormRow>
                        <label>Networks</label>
                        <Select 
                            options={filteredNetworks}
                            classNamePrefix={`select`}
                            onChange={onChangeSelectedNetwork} 
                            value={filteredNetworks.filter(n => selectedNetworks.includes(n.value))}
                            isClearable={true}
                            isMulti={true}
                        />
                    </FormRow>
                </div>

            </div>

            {playerSystemMonitorData.length ? <> <table className="new-alighned-table player-system-table">
                <colgroup>
                    <col /> 
                    <col /> 
                    <col />
                    <col />
                    <col />
                    <col />
                    <col style={{width:"250px"}} />
                    <col style={{width:"100px"}} />
                </colgroup>
                <thead>
                    <tr>
                        <td onClick={()=>{setSortData("Player")}}>Player <SortIndicator isAsc={isAscSort} isActive={checkCurrentSortColumn("Player", sortBy)}/></td>
                        <td onClick={()=>{setSortData("Network")}}>Network <SortIndicator isAsc={isAscSort} isActive={checkCurrentSortColumn("Network", sortBy)}/></td>
                        <td onClick={()=>{setSortData("CPU")}}>CPU Load <SortIndicator isAsc={isAscSort} isActive={checkCurrentSortColumn("CPU", sortBy)}/> </td>
                        <td onClick={()=>{setSortData("GPU")}}>GPU Load <SortIndicator isAsc={isAscSort} isActive={checkCurrentSortColumn("GPU", sortBy)}/></td>
                        <td onClick={()=>{setSortData("Space")}}>Space GB <SortIndicator isAsc={isAscSort} isActive={checkCurrentSortColumn("Space", sortBy)}/></td>
                        <td onClick={()=>{setSortData("Memory")}}>Memory <SortIndicator isAsc={isAscSort} isActive={checkCurrentSortColumn("Memory", sortBy)}/></td>
                        <td onClick={()=>{setSortData("LatestHeartbeat")}}>Latest Heartbeat <SortIndicator isAsc={isAscSort} isActive={checkCurrentSortColumn("LatestHeartbeat", sortBy)}/></td>
                        <td onClick={()=>{setSortData("Version")}}>Version <SortIndicator isAsc={isAscSort} isActive={checkCurrentSortColumn("Version", sortBy)}/></td>
                    </tr>
                </thead>
                <tbody>
                    {renderPlayerMonitor(sortedPlyerMonitor)}
                </tbody>
            </table>
            </> : <p>There are no items</p>}

            <GlobalSpinner isShow={isLoading} />
        </>
    )
}

export default PlayerSystemMonitor